import React from 'react';

import { format, parseISO } from 'date-fns';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';

import { Release, ReleaseChangeType } from '../../models/release.model';

import { isNewerVersion } from '../../releases/releaseNotes';
import Drawer from '../shared/Drawer';

// Changes are grouped under these headings, in this order.
const CHANGE_GROUPS: { type: ReleaseChangeType; heading: string }[] = [
    { type: 'feature', heading: 'New' },
    { type: 'improvement', heading: 'Improved' },
    { type: 'fix', heading: 'Fixed' },
];

interface WhatsNewDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    releases: Release[];
    /** Last seen version before this opening, used to highlight what's new. */
    previouslySeenVersion: string;
}

const WhatsNewDrawer: React.FC<WhatsNewDrawerProps> = ({
    isOpen,
    onClose,
    releases,
    previouslySeenVersion,
}) => (
    <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title="What's new"
        showCloseButton
    >
        <ReleaseList>
            {releases.map((release) => {
                const unseen = isNewerVersion(
                    release.version,
                    previouslySeenVersion,
                );
                return (
                    <ReleaseItem
                        key={release.version}
                        data-testid={`release-${release.version}`}
                        data-unseen={String(unseen)}
                        $unseen={unseen}
                    >
                        <ReleaseHeader>
                            <ReleaseVersion>v{release.version}</ReleaseVersion>
                            <ReleaseDate>
                                {format(parseISO(release.date), 'd MMM yyyy')}
                            </ReleaseDate>
                            {unseen && <NewLabel>New</NewLabel>}
                        </ReleaseHeader>
                        {CHANGE_GROUPS.map(({ type, heading }) => {
                            const changes = release.changes.filter(
                                (change) => change.type === type,
                            );
                            if (changes.length === 0) return null;
                            return (
                                <ChangeGroup
                                    key={type}
                                    data-testid={`change-group-${type}`}
                                >
                                    <GroupHeading>{heading}</GroupHeading>
                                    {changes.map((change) => (
                                        <Change key={change.title}>
                                            <ChangeTitle>
                                                {change.title}
                                            </ChangeTitle>
                                            <ChangeDescription>
                                                {change.description}
                                            </ChangeDescription>
                                        </Change>
                                    ))}
                                </ChangeGroup>
                            );
                        })}
                    </ReleaseItem>
                );
            })}
        </ReleaseList>
    </Drawer>
);

const ReleaseList = styled.ol({
    listStyle: 'none',
    margin: 0,
    padding: aampeTheme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
    overflowY: 'auto',
});

const ReleaseItem = styled.li<{ $unseen: boolean }>(({ $unseen }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
    padding: aampeTheme.spacing(1.5),
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid ${
        $unseen ? aampeTheme.colors.blue700 : aampeTheme.colors.blueGray50
    }`,
}));

const ReleaseHeader = styled.div({
    display: 'flex',
    alignItems: 'baseline',
    gap: aampeTheme.spacing(1),
});

const ReleaseVersion = styled.span({
    fontWeight: aampeTheme.weights.heavy,
    fontSize: aampeTheme.size.font.textMedium,
    color: aampeTheme.colors.textAccessibleWG02,
});

const ReleaseDate = styled.span({
    fontSize: aampeTheme.size.font.textSmall,
    color: aampeTheme.colors.textAccessibleWG01,
});

const NewLabel = styled.span({
    marginLeft: 'auto',
    fontSize: aampeTheme.size.font.textSmall,
    fontWeight: aampeTheme.weights.heavy,
    color: aampeTheme.colors.blue700,
});

const ChangeGroup = styled.section({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
});

const GroupHeading = styled.h3({
    margin: 0,
    fontSize: aampeTheme.size.font.textSmall,
    fontWeight: aampeTheme.weights.heavy,
    color: aampeTheme.colors.textAccessibleWG01,
});

const Change = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(0.5),
});

const ChangeTitle = styled.span({
    fontSize: aampeTheme.size.font.textMedium,
    fontWeight: aampeTheme.weights.heavy,
    color: aampeTheme.colors.textAccessibleWG02,
});

const ChangeDescription = styled.span({
    fontSize: aampeTheme.size.font.textSmall,
    color: aampeTheme.colors.textAccessibleWG01,
});

export default WhatsNewDrawer;
